// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  useDroppable
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileText, FileSignature, X, Clock, Filter, GripVertical, Calendar, MessageSquare, Paperclip, Phone,
  Mail, ArrowUpCircle, ArrowRightCircle, ArrowDownCircle, RotateCcw
} from 'lucide-react';
import AddressAutocomplete, { type AddressResult } from '@easyflow/ui-frontend/components/AddressAutocomplete';
import { masterAddressFetch } from '@/lib/masterAddressFetch';
import type {
  InputFieldProps, SelectFieldProps, ModalWrapperProps
} from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type { CrmMasterPipelineEntry as PipelineEntry } from '@easyflow/shared';

export function isUserTypingInForm(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    const t = (el as HTMLInputElement).type;
    if (t === 'checkbox' || t === 'radio' || t === 'button' || t === 'submit') return false;
    return true;
  }
  if (el.isContentEditable) return true;
  return false;
}

export function Tip({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const ref = React.useRef<HTMLSpanElement>(null);
  const show = () => { if (ref.current) { const r = ref.current.getBoundingClientRect(); setPos({ top: r.top - 4, left: r.left + r.width / 2 }); } };
  const hide = () => setPos(null);
  return (
    <span ref={ref} className={`inline-flex ${className}`} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {pos && ReactDOM.createPortal(
        <span style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translate(-50%, -100%)', zIndex: 99999 }}
          className="pointer-events-none px-2 py-1 rounded bg-gray-900 text-white text-[11px] font-medium whitespace-nowrap shadow-lg border border-gray-700 animate-in fade-in-0 duration-100">
          {label}
          <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900" />
        </span>,
        document.body
      )}
    </span>
  );
}

export const pipelineColors: Record<string, string> = {
  lead: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  contacted: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  demo: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  quote_sent: 'bg-red-500/20 text-red-400 border-red-500/30',
  negotiation: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  won: 'bg-green-500/20 text-green-400 border-green-500/30',
  lost: 'bg-muted/500/20 text-muted-foreground/70 border-gray-500/30',
  archived: 'bg-gray-400/20 text-muted-foreground border-gray-400/30',
};

export const pipelineLabels: Record<string, string> = {
  lead: 'Lead', contacted: 'Contattato', demo: 'Demo Effettuata',
  quote_sent: 'Preventivo Inviato', negotiation: 'Trattativa',
  won: 'Vinto/Attivo', lost: 'Perso', archived: 'Archiviato',
};

export const stageColumnColors: Record<string, string> = {
  lead: 'border-blue-500', contacted: 'border-purple-500',
  demo: 'border-amber-500', quote_sent: 'border-red-500',
  negotiation: 'border-pink-500', won: 'border-green-500', lost: 'border-gray-500',
  archived: 'border-gray-400',
};

export const LOCKED_STAGE_SLUGS = ['negotiation', 'won', 'lost', 'archived'];

export const recurrenceBadge: Record<string, string> = {
  one_time: 'bg-blue-500/20 text-blue-400',
  monthly: 'bg-green-500/20 text-green-400',
  quarterly: 'bg-orange-500/20 text-orange-400',
  annual: 'bg-purple-500/20 text-purple-400',
};

export const recurrenceLabels: Record<string, string> = {
  one_time: 'Una Tantum', monthly: 'Mensile', quarterly: 'Trimestrale', annual: 'Annuale',
};

export const natureLabels: Record<string, string> = {
  servizio: 'Servizio', prodotto: 'Prodotto', canvas: 'Canvas',
};

export const natureBadge: Record<string, string> = {
  servizio: 'bg-sky-500/20 text-sky-400',
  prodotto: 'bg-indigo-500/20 text-indigo-400',
  canvas: 'bg-fuchsia-500/20 text-fuchsia-400',
};

export const productStatusLabels: Record<string, string> = {
  active: 'Attivo', inactive: 'Disattivo', archived: 'Archiviato',
};

export const productStatusBadge: Record<string, string> = {
  active: 'bg-green-500/20 text-green-400',
  inactive: 'bg-amber-500/20 text-amber-400',
  archived: 'bg-muted/500/20 text-muted-foreground/70',
};

// Un prodotto è idoneo a comparire nei listini se attivo e nel periodo di validità (mirror del gate backend).
// Task #3875: accetta string|Date (wire = ISO string, tipo condiviso = union).
export function isProductEligible(p: { status?: string; valid_from?: string | Date | null; valid_to?: string | Date | null }): boolean {
  if (p.status !== 'active') return false;
  const today = new Date().toISOString().slice(0, 10);
  if (p.valid_from && String(p.valid_from).slice(0, 10) > today) return false;
  if (p.valid_to && String(p.valid_to).slice(0, 10) < today) return false;
  return true;
}

export const quoteStatusBadge: Record<string, string> = {
  draft: 'bg-muted/500/20 text-muted-foreground/70',
  sent: 'bg-blue-500/20 text-blue-400',
  revision: 'bg-amber-500/20 text-amber-400',
  accepted: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400',
  expired: 'bg-orange-500/20 text-orange-400',
  payment_setup_sent: 'bg-purple-500/20 text-purple-400',
  payment_confirmed: 'bg-emerald-500/20 text-emerald-400',
  contract_created: 'bg-teal-500/20 text-teal-400',
};

export const quoteStatusLabels: Record<string, string> = {
  draft: 'Bozza', sent: 'Inviato', revision: 'Da Rivedere',
  accepted: 'Accettato', rejected: 'Rifiutato', expired: 'Scaduto',
  payment_setup_sent: 'Setup Pagamento Inviato',
  payment_confirmed: 'Pagamento Confermato',
  contract_created: 'Contratto Creato',
};

export const contractStatusBadge: Record<string, string> = {
  draft: 'bg-muted/500/20 text-muted-foreground/70',
  sent: 'bg-blue-500/20 text-blue-400',
  pending_signature: 'bg-amber-500/20 text-amber-400',
  signed: 'bg-green-500/20 text-green-400',
  cancelled: 'bg-red-500/20 text-red-400',
};

export const contractStatusLabels: Record<string, string> = {
  draft: 'Bozza', sent: 'Inviato', pending_signature: 'In Attesa Firma',
  signed: 'Firmato', cancelled: 'Annullato',
};

export const contractPipelineStages = [
  { key: 'inserito', label: 'Inserito', color: 'bg-muted/500', border: 'border-gray-500', badge: 'bg-muted/500/20 text-muted-foreground/70', icon: '📋' },
  { key: 'inviato', label: 'Inviato', color: 'bg-blue-500', border: 'border-blue-500', badge: 'bg-blue-500/20 text-blue-400', icon: '📤' },
  { key: 'firmato', label: 'Firmato', color: 'bg-purple-500', border: 'border-purple-500', badge: 'bg-purple-500/20 text-purple-400', icon: '✍️' },
  { key: 'attivo', label: 'Attivo', color: 'bg-green-500', border: 'border-green-500', badge: 'bg-green-500/20 text-green-400', icon: '✅' },
  { key: 'in_scadenza', label: 'In Scadenza', color: 'bg-amber-500', border: 'border-amber-500', badge: 'bg-amber-500/20 text-amber-400', icon: '⏳' },
  { key: 'scaduto', label: 'Scaduto', color: 'bg-red-500', border: 'border-red-500', badge: 'bg-red-500/20 text-red-400', icon: '❌' },
  { key: 'rinnovato', label: 'Rinnovato', color: 'bg-emerald-500', border: 'border-emerald-500', badge: 'bg-emerald-500/20 text-emerald-400', icon: '🔄' },
];

export const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

export const sdiStatusBadge: Record<string, string> = {
  draft: 'bg-muted/500/20 text-muted-foreground/70',
  issued: 'bg-blue-500/20 text-blue-400',
  sent_sdi: 'bg-amber-500/20 text-amber-400',
  accepted_sdi: 'bg-green-500/20 text-green-400',
  rejected_sdi: 'bg-red-500/20 text-red-400',
  paid: 'bg-emerald-500/20 text-emerald-400',
  partially_paid: 'bg-yellow-500/20 text-yellow-400',
  overdue: 'bg-red-500/20 text-red-400',
  credited: 'bg-orange-500/20 text-orange-400',
  sdi_error: 'bg-red-500/20 text-red-400',
};

export const ITALIAN_VAT_RATES = [
  { code: '22', rate: 22, label: '22% - Ordinaria', is_taxable: true },
  { code: '10', rate: 10, label: '10% - Ridotta', is_taxable: true },
  { code: '5', rate: 5, label: '5% - Super ridotta', is_taxable: true },
  { code: '4', rate: 4, label: '4% - Minima', is_taxable: true },
  { code: 'N1', rate: 0, label: 'Escluse art. 15', is_taxable: false },
  { code: 'N2.1', rate: 0, label: 'Non soggette - Territorio', is_taxable: false },
  { code: 'N2.2', rate: 0, label: 'Non soggette - Altri casi', is_taxable: false },
  { code: 'N3.1', rate: 0, label: 'Non imp. - Esportazioni', is_taxable: false },
  { code: 'N3.2', rate: 0, label: 'Non imp. - Intracomunitarie', is_taxable: false },
  { code: 'N3.3', rate: 0, label: 'Non imp. - San Marino', is_taxable: false },
  { code: 'N3.4', rate: 0, label: 'Non imp. - Assimilate export', is_taxable: false },
  { code: 'N3.5', rate: 0, label: "Non imp. - Dich. d'intento", is_taxable: false },
  { code: 'N3.6', rate: 0, label: 'Non imp. - Altre non plafond', is_taxable: false },
  { code: 'N4', rate: 0, label: 'Esenti art. 10', is_taxable: false },
  { code: 'N5', rate: 0, label: 'Regime del margine', is_taxable: false },
  { code: 'N6.1', rate: 0, label: 'Rev. charge - Rottami', is_taxable: false },
  { code: 'N6.2', rate: 0, label: 'Rev. charge - Oro/Argento', is_taxable: false },
  { code: 'N6.3', rate: 0, label: 'Rev. charge - Subappalto edile', is_taxable: false },
  { code: 'N6.4', rate: 0, label: 'Rev. charge - Fabbricati', is_taxable: false },
  { code: 'N6.5', rate: 0, label: 'Rev. charge - Cellulari', is_taxable: false },
  { code: 'N6.6', rate: 0, label: 'Rev. charge - Elettronici', is_taxable: false },
  { code: 'N6.7', rate: 0, label: 'Rev. charge - Edile/Connessi', is_taxable: false },
  { code: 'N6.8', rate: 0, label: 'Rev. charge - Energetico', is_taxable: false },
  { code: 'N6.9', rate: 0, label: 'Rev. charge - Altri casi', is_taxable: false },
  { code: 'N7', rate: 0, label: 'IVA assolta in altro stato UE', is_taxable: false },
];

export function getVatRate(code: string): number {
  const found = ITALIAN_VAT_RATES.find(v => v.code === code);
  return found ? found.rate : (parseFloat(code) || 22);
}

export function VatSelect({ value, onChange, className = '' }: { value: string; onChange: (v: string) => void; className?: string }) {
  return (
    <select value={value || '22'} onChange={(e) => onChange?.(e.target.value)}
      className={`w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-inset focus:ring-blue-500 focus:border-transparent outline-none truncate ${className}`}>
      <optgroup label="Aliquote">
        {ITALIAN_VAT_RATES.filter(v => v.is_taxable).map(v => (
          <option key={v.code} value={v.code}>{v.label}</option>
        ))}
      </optgroup>
      <optgroup label="Esenti / Non imp.">
        {ITALIAN_VAT_RATES.filter(v => !v.is_taxable).map(v => (
          <option key={v.code} value={v.code}>{v.label}</option>
        ))}
      </optgroup>
    </select>
  );
}

export function InputField({ label, value, onChange, type = 'text', placeholder = '', readOnly = false, className = '', error = false, required = false, uppercase = false }: InputFieldProps) {
  const [focused, setFocused] = useState(false);
  const showHint = type === 'password' && !value && placeholder && !focused;
  const borderClass = error
    ? 'border-red-500 dark:border-red-500 ring-1 ring-red-500/30'
    : 'border-border dark:border-gray-600';
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {showHint ? (
        <div
          onClick={() => setFocused(true)}
          className={`w-full bg-muted dark:bg-gray-700 border ${borderClass} text-muted-foreground/70 dark:text-muted-foreground rounded-lg px-3 py-2 text-sm cursor-text`}
        >{placeholder}</div>
      ) : (
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            setFocused(false);
            // Task #1503: nominativi normalizzati in UPPERCASE on blur (feedback UI;
            // backend Zod transform riapplica comunque la normalizzazione).
            if (uppercase) {
              const upper = (e.target.value || '').toLocaleUpperCase('it-IT');
              if (upper !== value) onChange?.(upper);
            }
          }}
          placeholder={placeholder}
          readOnly={readOnly}
          autoFocus={focused && type === 'password'}
          className={`w-full bg-muted dark:bg-gray-700 border ${borderClass} text-foreground dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-inset focus:ring-blue-500 focus:border-transparent outline-none`}
        />
      )}
      {error && typeof error === 'string' && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

export function AddressField({ label = 'Cerca indirizzo', value, onSelect, className = '' }: { label?: string; value?: string; onSelect: (a: AddressResult) => void; className?: string }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">{label}</label>
      <AddressAutocomplete
        defaultValue={value}
        placeholder="Cerca indirizzo..."
        fetchSuggestions={masterAddressFetch}
        onAddressSelect={onSelect}
      />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, className = '', error = false, required = false }: SelectFieldProps) {
  const borderClass = error
    ? 'border-red-500 dark:border-red-500 ring-1 ring-red-500/30'
    : 'border-border dark:border-gray-600';
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        className={`w-full bg-muted dark:bg-gray-700 border ${borderClass} text-foreground dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-inset focus:ring-blue-500 focus:border-transparent outline-none`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {error && typeof error === 'string' && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

export function Badge({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${className}`}>
      {children}
    </span>
  );
}

export function ModalWrapper({ open, onClose, title, children, wide = false }: ModalWrapperProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 dark:bg-black/60 z-50" />
        <Dialog.Content
          aria-describedby={undefined}
          className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl shadow-2xl z-50 p-6 ${wide ? 'w-[90vw] max-w-4xl' : 'w-full max-w-lg'} max-h-[85vh] overflow-y-auto`}
        >
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-semibold text-foreground dark:text-white">{title}</Dialog.Title>
            <Dialog.Close className="text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function DroppableColumn({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`min-h-[280px] flex-1 transition-all duration-200 rounded-xl ${isOver ? 'bg-blue-50/50 dark:bg-blue-900/10 ring-2 ring-blue-300/40 dark:ring-blue-500/30' : ''}`}>
      {children}
    </div>
  );
}

export const leadSourceLabels: Record<string, string> = {
  web_form: 'Web Form', phone: 'Telefono', referral: 'Referral', email: 'Email',
  social: 'Social', event: 'Evento', other: 'Altro',
};
export const leadSourceColors: Record<string, string> = {
  web_form: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  phone: 'bg-green-500/20 text-green-400 border-green-500/30',
  referral: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  email: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  social: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  event: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  other: 'bg-muted/500/20 text-muted-foreground/70 border-gray-500/30',
};
export const priorityConfig: Record<string, { color: string; border: string; Icon: React.ComponentType<{ className?: string }> }> = {
  high: { color: 'bg-red-500', border: 'border-l-red-500', Icon: ArrowUpCircle },
  medium: { color: 'bg-amber-500', border: 'border-l-amber-500', Icon: ArrowRightCircle },
  low: { color: 'bg-green-500', border: 'border-l-green-500', Icon: ArrowDownCircle },
};

export function DraggableCard({ entry, onClick, stageColor }: { entry: PipelineEntry; onClick: () => void; stageColor?: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: entry.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, scale: isDragging ? '1.02' : '1' };
  const timeInStage = entry.entered_at
    ? Math.floor((Date.now() - new Date(entry.entered_at).getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const color = stageColor || '#6B7280';
  const prio = priorityConfig[entry.priority ?? ''];
  const contactInfo = entry.contact_email || entry.contact_phone;

  return (
    <div ref={setNodeRef} style={style} {...attributes}
      className={`bg-white dark:bg-gray-800 border border-border/80 dark:border-gray-700/60 rounded-xl p-3.5 mb-2.5 cursor-pointer hover:shadow-md dark:hover:shadow-lg hover:shadow-gray-200/50 dark:hover:shadow-black/20 hover:border-border dark:hover:border-gray-600 transition-all duration-200 group ${prio ? `border-l-2 ${prio.border}` : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}40` }} />
            <p className="text-foreground dark:text-white text-sm font-semibold truncate">{entry.company_name || entry.tenant_name || entry.contact_name || 'N/A'}</p>
            {prio && <prio.Icon className={`h-3.5 w-3.5 flex-shrink-0 ${entry.priority === 'high' ? 'text-red-500' : entry.priority === 'medium' ? 'text-amber-500' : 'text-green-500'}`} />}
          </div>
          {contactInfo && (
            <p className="text-[11px] text-muted-foreground dark:text-muted-foreground/70 mt-0.5 ml-4 truncate flex items-center gap-1">
              {entry.contact_email ? <Mail className="h-3 w-3 flex-shrink-0" /> : <Phone className="h-3 w-3 flex-shrink-0" />}
              {contactInfo}
            </p>
          )}
          <div className="flex items-center gap-1.5 mt-1.5 ml-4 flex-wrap">
            {entry.lead_source && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${leadSourceColors[entry.lead_source] || 'bg-muted/500/20 text-muted-foreground/70 border-gray-500/30'}`}>
                {leadSourceLabels[entry.lead_source] || entry.lead_source}
              </span>
            )}
            {Number(entry.opportunity_value) > 0 && (
              <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold">€ {Number(entry.opportunity_value).toLocaleString('it-IT', { minimumFractionDigits: 0 })}</span>
            )}
          </div>
        </div>
        <div {...listeners} className="text-muted-foreground/60 dark:text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground/70 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical className="h-4 w-4" />
        </div>
      </div>
      {entry.next_followup_at && (
        <div className="flex items-center gap-1 mt-2 ml-4 text-[11px] text-blue-500 dark:text-blue-400">
          <Calendar className="h-3 w-3" />
          <span>{new Date(entry.next_followup_at).toLocaleDateString('it-IT')}</span>
        </div>
      )}
      <div className="flex items-center justify-between mt-2.5 ml-4">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70 dark:text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>{timeInStage === 0 ? 'Oggi' : `${timeInStage}gg`}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {(entry.notes_count ?? 0) > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground/70 dark:text-muted-foreground" title={`${entry.notes_count} note`}>
                <MessageSquare className="h-3 w-3" />{entry.notes_count}
              </span>
            )}
            {(entry.attachments_count ?? 0) > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground/70 dark:text-muted-foreground" title={`${entry.attachments_count} allegati`}>
                <Paperclip className="h-3 w-3" />{entry.attachments_count}
              </span>
            )}
            {(entry.quotes_count ?? 0) > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground/70 dark:text-muted-foreground" title={`${entry.quotes_count} preventivi`}>
                <FileText className="h-3 w-3" />{entry.quotes_count}
              </span>
            )}
            {(entry.contracts_count ?? 0) > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground/70 dark:text-muted-foreground" title={`${entry.contracts_count} contratti`}>
                <FileSignature className="h-3 w-3" />{entry.contracts_count}
              </span>
            )}
          </div>
          {entry.assigned_to && (
            <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <span className="text-[10px] font-bold text-muted-foreground dark:text-muted-foreground/70">{entry.assigned_to.charAt(0).toUpperCase()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export interface AdvFilters {
  dateFrom: string;
  dateTo: string;
  customer: string;
  amountMin: string;
  amountMax: string;
}
export const EMPTY_ADV_FILTERS: AdvFilters = { dateFrom: '', dateTo: '', customer: '', amountMin: '', amountMax: '' };

export function countAdvFilters(f: AdvFilters): number {
  return (Object.values(f) as string[]).filter((v) => v.trim() !== '').length;
}

// Task #3098 — Badge che segnala che KPI/grafici riflettono il set filtrato.
export function FilteredBadge({ filters }: { filters: AdvFilters }) {
  const n = countAdvFilters(filters);
  if (n === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500/10 text-blue-500 dark:text-blue-400 text-xs font-medium">
      <Filter className="h-3 w-3" /> Dati filtrati ({n})
    </span>
  );
}

// Serializza i filtri avanzati come query string per il backend.
// `dateKeys` gestisce la differenza di naming (quotes/contracts/invoices usano
// date_from/date_to, le rate installments usano from_date/to_date).
export function advFiltersToParams(f: AdvFilters, dateKeys: { from: string; to: string }): URLSearchParams {
  const p = new URLSearchParams();
  if (f.dateFrom.trim()) p.set(dateKeys.from, f.dateFrom.trim());
  if (f.dateTo.trim()) p.set(dateKeys.to, f.dateTo.trim());
  if (f.customer.trim()) p.set('customer_search', f.customer.trim());
  if (f.amountMin.trim()) p.set('amount_min', f.amountMin.trim());
  if (f.amountMax.trim()) p.set('amount_max', f.amountMax.trim());
  return p;
}

export function AdvancedFilters({
  filters,
  onChange,
  dateLabel = 'Data',
}: {
  filters: AdvFilters;
  onChange: (f: AdvFilters) => void;
  dateLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const active = countAdvFilters(filters);
  const inputCls =
    'bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm w-full';
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
          active > 0
            ? 'border-blue-500 text-blue-500 dark:text-blue-400'
            : 'border-border dark:border-gray-600 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white'
        }`}
      >
        <Filter className="h-4 w-4" /> Filtri avanzati
        {active > 0 && (
          <span className="ml-1 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-blue-600 text-white text-[11px] font-semibold">
            {active}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute z-30 mt-2 right-0 w-80 bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl shadow-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground dark:text-white">Filtri avanzati</span>
            <button type="button" onClick={() => setOpen(false)} className="text-muted-foreground/70 hover:text-foreground dark:hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground dark:text-muted-foreground/70">{dateLabel} (da / a)</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={filters.dateFrom} onChange={(e) => onChange({ ...filters, dateFrom: e.target.value })} className={inputCls} />
              <input type="date" value={filters.dateTo} onChange={(e) => onChange({ ...filters, dateTo: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground dark:text-muted-foreground/70">Cliente</label>
            <input
              type="text"
              value={filters.customer}
              onChange={(e) => onChange({ ...filters, customer: e.target.value })}
              placeholder="Nome cliente o lead..."
              className={inputCls}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground dark:text-muted-foreground/70">Importo (min / max)</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" min="0" step="0.01" value={filters.amountMin} onChange={(e) => onChange({ ...filters, amountMin: e.target.value })} placeholder="0" className={inputCls} />
              <input type="number" min="0" step="0.01" value={filters.amountMax} onChange={(e) => onChange({ ...filters, amountMax: e.target.value })} placeholder="∞" className={inputCls} />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={() => onChange(EMPTY_ADV_FILTERS)}
              disabled={active === 0}
              className="flex items-center gap-1 text-sm text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white disabled:opacity-40"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
            <span className="text-xs text-muted-foreground/70">{active} attivo{active === 1 ? '' : 'i'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

